import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { config } from '../lib/config';
import { ApiError, partnerConfirmForgotPassword, partnerForgotPassword } from '../lib/identityAuth';
import { PasswordField } from '../components/PasswordField';
import { BrandLogo } from '../components/BrandLogo';

type Step = 'request' | 'reset';

const RESEND_SECONDS = config.resendCooldownSeconds;

function messageForError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'VALIDATION_ERROR':
        return fallback;
      default:
        return err.message || fallback;
    }
  }
  return err instanceof Error ? err.message : fallback;
}

export function PartnerForgotPasswordPage(): JSX.Element {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback((seconds: number) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((current) => {
        if (current <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  async function handleRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await partnerForgotPassword(email);
      setStep('reset');
      setNotice(`We sent a reset code to ${email}.`);
      startCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(messageForError(err, 'Could not start password recovery for that email.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await partnerConfirmForgotPassword(email, code.trim(), newPassword);
      navigate('/partner/login', {
        replace: true,
        state: { notice: 'Password updated. Sign in with your new password.' },
      });
    } catch (err) {
      setError(messageForError(err, 'Invalid or expired code, or the email is not registered.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await partnerForgotPassword(email);
      setNotice('A new code is on its way.');
      startCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(messageForError(err, 'Could not resend the code.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>{step === 'request' ? 'Reset your partner password' : 'Enter your reset code'}</h1>
        <p className="login-card__copy">
          {step === 'request'
            ? 'Enter your partner account email and we will send you a code to reset your password.'
            : `Enter the code we emailed to ${email} and choose a new password.`}
        </p>

        {step === 'request' ? (
          <form className="login-form" onSubmit={handleRequest}>
            <label>
              <span>Email</span>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                type="email"
                required
                autoComplete="username"
              />
            </label>

            {error ? <div className="form-error">{error}</div> : null}
            {notice ? <p className="hint-text">{notice}</p> : null}

            <button className="primary-button" disabled={loading} type="submit">
              {loading ? 'Sending code...' : 'Send reset code'}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handleReset}>
            <label>
              <span>Reset code</span>
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>

            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />

            <PasswordField
              label="Confirm new password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              minLength={config.passwordMinLength}
              autoComplete="new-password"
            />

            {error ? <div className="form-error">{error}</div> : null}
            {notice ? <p className="hint-text">{notice}</p> : null}

            <button className="primary-button" disabled={loading} type="submit">
              {loading ? 'Updating...' : 'Set new password'}
            </button>

            <button
              className="secondary-button"
              type="button"
              disabled={loading || cooldown > 0}
              onClick={handleResend}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        <p className="hint-text">
          Remembered it?{' '}
          <Link className="text-link" to="/partner/login">
            Back to partner sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
